import { CSV } from "@repo/lib/csv";
import { type DotNotation, getByDotPath } from "@repo/lib/dotPath";
import { tenantStore } from "@/stores/tenantStore";
import type {
  Agenda,
  AgendaItem,
  AgendaModule,
  Document,
  Meeting,
  Tag,
  Team,
  User,
} from "@repo/types";
import { formatDate } from "date-fns";
import { agendaRepo } from "@/repositories/agendaRepo";
import { firstValueFrom } from "rxjs";
import { meetingRepo } from "@/repositories/meetingRepo";
import { teamRepo } from "@/repositories/teamRepo";
import { userRepo } from "@/repositories/userRepo";
import { agendaModuleRepo } from "@/repositories/agendaModuleRepo";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";
import { documentRepo } from "@/repositories/documentRepo";

//#region Base Service

export class BaseTableExportService<T> {
  public readonly config: ExportConfig<T>;

  public constructor(config: ExportConfig<T>) {
    this.config = config;
  }

  private async recordToString(
    record: T,
    context: ExportContext,
    fields?: string[]
  ): Promise<ExportRow> {
    let selectedFields = new Map<string, FieldExportConfig<T>>();
    if (fields && fields.length > 0) {
      for (const field of fields) {
        const fieldConfig = this.config[field];
        if (fieldConfig) {
          selectedFields.set(field, fieldConfig);
        }
      }
    } else {
      selectedFields = new Map<string, FieldExportConfig<T>>(
        Object.entries(this.config)
      );
    }
    const data = Object.create(null) as ExportRow;
    for (const [_, fieldConfig] of selectedFields.entries()) {
      const values = {} as Record<string, unknown>;
      const columns = new Map<string, DotNotation<T>>(
        Object.entries(fieldConfig.columns)
      );
      columns.forEach((path, key) => {
        const columnValue = getByDotPath(record, path);
        values[key] = columnValue;
      });
      const convertedValue = await fieldConfig.fieldConverter(
        values,
        context,
        record
      );
      data[fieldConfig.resultFieldname] = convertedValue;
    }

    return data;
  }

  private async convertRecords({
    rows,
    fields,
    onProgress,
  }: {
    rows: T[];
    fields?: string[];
    onProgress?: (progress: number) => void;
  }): Promise<ExportRow[]> {
    const exportedItems: ExportRow[] = [];
    const context = new ExportContext();

    onProgress?.(0);

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const exportedItem = await this.recordToString(row, context, fields);
      exportedItems.push(exportedItem);

      onProgress?.((i + 1) / rows.length);
    }

    return exportedItems;
  }

  async getCSV({
    rows,
    fields,
    onProgress,
  }: {
    rows: T[];
    fields?: string[];
    onProgress?: (progress: number) => void;
  }) {
    const selectedFields = fields || Object.keys(this.config);
    const headers = selectedFields.map(
      (f) => this.config[f]?.resultFieldname || f
    );
    const converted = await this.convertRecords({
      rows,
      fields: selectedFields,
      onProgress,
    });

    const csv = new CSV(headers);
    for (const item of converted) {
      csv.addEntry(item);
    }
    return csv.toString();
  }
}

//#endregion

//#region Types

export type ExportConfig<T> = {
  [Key in DotNotation<T> | string]: FieldExportConfig<T>;
};

export type FieldExportConfig<T> = {
  alias: DotNotation<T> | string;
  columns: Record<string, DotNotation<T>>;
  fieldConverter: FieldConverter;
  resultFieldname: string;
};

export type FieldConverter = (
  values: Record<string, unknown>,
  context: ExportContext,
  object: unknown
) => Promise<string> | string;

export type ExportRow = {
  [column: string]: string;
};

export class ExportContext {
  private users = new Map<string, User | null>();
  private teams = new Map<string, Team | null>();
  private meetings = new Map<string, Meeting | null>();
  private agendas = new Map<string, Agenda | null>();
  private agendaModules = new Map<string, AgendaModule | null>();
  private agendaItems = new Map<string, AgendaItem | null>();
  private documents = new Map<string, Document | null>();
  private columnState: Record<string, unknown> = Object.create(null);

  async getUser(userId: string): Promise<User | undefined> {
    let data = this.users.get(userId);
    if (data === undefined) {
      data = await firstValueFrom(userRepo.get(userId));
      this.users.set(userId, data);
    }
    return data ?? undefined;
  }

  async getTeam(tenantId: string, teamId: string): Promise<Team | undefined> {
    const key = `tenant.${tenantId}|team.${teamId}`;
    let data = this.teams.get(key);
    if (data === undefined) {
      data = await firstValueFrom(teamRepo.get(tenantId, teamId));
      this.teams.set(key, data);
    }
    return data ?? undefined;
  }

  async getMeeting(
    tenantId: string,
    meetingId: string
  ): Promise<Meeting | undefined> {
    const key = `tenant.${tenantId}|meeting.${meetingId}`;
    let data = this.meetings.get(key);
    if (data === undefined) {
      data = await firstValueFrom(meetingRepo.get(tenantId, meetingId));
      this.meetings.set(key, data);
    }
    return data ?? undefined;
  }

  async getAgenda(
    tenantId: string,
    agendaId: string
  ): Promise<Agenda | undefined> {
    const key = `tenant.${tenantId}|agenda.${agendaId}`;
    let data = this.agendas.get(key);
    if (data === undefined) {
      data = await firstValueFrom(agendaRepo.get(tenantId, agendaId));
      this.agendas.set(key, data);
    }
    return data ?? undefined;
  }

  async getAgendaModule(
    tenantId: string,
    agendaModuleId: string
  ): Promise<AgendaModule | undefined> {
    const key = `tenant.${tenantId}|agendaModule.${agendaModuleId}`;
    let data = this.agendaModules.get(key);
    if (data === undefined) {
      data =
        (await firstValueFrom(
          agendaModuleRepo.get(tenantId, agendaModuleId)
        )) ?? null;
      this.agendaModules.set(key, data);
    }
    return data ?? undefined;
  }

  async getAgendaItem(
    tenantId: string,
    agendaItemId: string
  ): Promise<AgendaItem | undefined> {
    const key = `tenant.${tenantId}|agendaItem.${agendaItemId}`;
    let data = this.agendaItems.get(key);
    if (data === undefined) {
      data = await firstValueFrom(agendaItemRepo.get(tenantId, agendaItemId));
      this.agendaItems.set(key, data);
    }
    return data ?? undefined;
  }

  async getDocument(
    tenantId: string,
    documentId: string
  ): Promise<Document | undefined> {
    const key = `tenant.${tenantId}|document.${documentId}`;
    let data = this.documents.get(key);
    if (data === undefined) {
      data = await firstValueFrom(documentRepo.get(tenantId, documentId));
      this.documents.set(key, data);
    }
    return data ?? undefined;
  }

  getCustomState<T extends object>(stateKey: string): T {
    return (this.columnState[stateKey] ??= {}) as T;
  }
}

//#endregion

//#region Converters

export class BaseExportConverters {
  toStringConverter: FieldConverter = (values) => {
    if (values.value === null || values.value === undefined) {
      return "";
    }
    if (typeof values.value === "string") {
      return values.value;
    }
    if (typeof values.value === "number") {
      return values.value.toString();
    }
    if (typeof values.value === "boolean") {
      return values.value.toString();
    }
    if (values.value instanceof Date) {
      return values.value.toISOString();
    }
    throw new Error("Invalid field type for string conversion");
  };

  booleanToYesNoConverter: FieldConverter = (values) => {
    if (
      typeof values.value === "boolean" ||
      values.value === null ||
      values.value === undefined
    ) {
      return values.value ? "Yes" : "No";
    }
    throw new Error("Invalid field type for boolean conversion");
  };

  tagsConverter: FieldConverter = (values) => {
    if (Array.isArray(values.value as Tag)) {
      return (values.value as Tag[]).map((tag) => tag.text).join(", ");
    }
    if (!values.value) {
      return "";
    }
    throw new Error("Invalid field type for tags conversion");
  };

  userConverter: FieldConverter = async (values, context) => {
    if (values.value === null || values.value === undefined) {
      return "Unknown";
    }
    if (typeof values.value === "string") {
      const userInField = await context.getUser(values.value);
      if (userInField) {
        return `${userInField.firstName} ${userInField.lastName}`.trim();
      }
      return values.value; // Fallback to user ID if not found
    }
    throw new Error("Invalid field type for user conversion");
  };

  teamConverter: FieldConverter = async (values, context) => {
    if (!values.value || values.value === "org") {
      return "Unknown";
    }
    if (typeof values.value === "string") {
      const tenantId = tenantStore.getState().id;
      const teamInField = await context.getTeam(tenantId, values.value);
      if (teamInField && teamInField.name) {
        return teamInField.name;
      }
      return values.value;
    }
    throw new Error("Invalid field type for team conversion");
  };

  approverConverter: FieldConverter = async (values, context) => {
    if (!values.value) {
      return "No approval required";
    }
    if (
      typeof values.value === "object" &&
      "approverType" in values.value &&
      "approverId" in values.value
    ) {
      const { approverType, approverId } = values.value as {
        approverType: string;
        approverId: string;
      };
      if (approverType === "user") {
        const userInField = await context.getUser(approverId);
        return userInField
          ? `${userInField.firstName} ${userInField.lastName}`
          : "<Unknown User>";
      } else if (approverType === "team") {
        const tenantId = tenantStore.getState().id;
        const teamInField = await context.getTeam(tenantId, approverId);
        return teamInField ? teamInField.name : "<Unknown Team>";
      }
      return "<Unknown Approver Type>";
    }
    return "<Unknown Approver>";
  };

  arrayToStringConverter: FieldConverter = (values) => {
    if (Array.isArray(values.value)) {
      return values.value.join("\n");
    }
    if (!values.value) {
      return "";
    }
    throw new Error("Invalid field type for array conversion");
  };

  dateFormatConverterFactory =
    (format: string): FieldConverter =>
    (values) => {
      if (values.value instanceof Date) {
        return formatDate(values.value, format);
      }
      if (!values.value) {
        return "";
      }
      throw new Error("Invalid field type for date format conversion");
    };

  get formatDateConverter() {
    return this.dateFormatConverterFactory("yyyy-MM-dd");
  }

  get formatTimeConverter() {
    return this.dateFormatConverterFactory("h:mm a");
  }
}

//#endregion
